import { CloseRounded, SearchRounded } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import useColors from "../../api/hooks/useColors";

const SearchInput = ({
  fieldName,
  appliedFilters,
  setAppliedFilters,
  minSearchLength = 1,
  placeholder = "Search",
  ...boxProps
}) => {
  const theme = useTheme();
  const colors = useColors();

  const appliedFilterValue = appliedFilters[fieldName] || "";
  const [inputValue, setInputValue] = useState(appliedFilterValue);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setInputValue(appliedFilterValue);
    }
  }, [appliedFilterValue, isFocused]);

  const updateFilter = (value) => {
    const shouldApplyFilter =
      value.length >= minSearchLength || value.length === 0;

    if (shouldApplyFilter) {
      setAppliedFilters({
        ...appliedFilters,
        [fieldName]: value,
      });
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    updateFilter(value);
  };

  const handleClear = () => {
    setInputValue("");
    setAppliedFilters({
      ...appliedFilters,
      [fieldName]: "",
    });
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const showMinLengthHelper = useMemo(
    () =>
      inputValue.length > 0 &&
      inputValue.length < minSearchLength &&
      minSearchLength > 1,
    [inputValue.length, minSearchLength]
  );

  const showClearButton = inputValue.length > 0;

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      position="relative"
      {...boxProps}
    >
      <TextField
        placeholder={placeholder}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        InputProps={{
          sx: {
            fontFamily: theme.typography.fontFamily,
            color: colors.contrast_text_dark,
            fontSize: "14px",
          },
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded
                style={{
                  color: colors.text_disabled,
                  width: "16px",
                  height: "16px",
                }}
              />
            </InputAdornment>
          ),
          endAdornment: showClearButton ? (
            <InputAdornment position="end">
              <IconButton
                onClick={handleClear}
                size="small"
                sx={{
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <CloseRounded
                  style={{
                    color: colors.text_disabled,
                    width: "16px",
                    height: "16px",
                  }}
                />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          backgroundColor: colors.common_white,
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: "8px",
            borderColor: colors.grey_32percent,
          },
          "& .MuiOutlinedInput-root": {
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "0.5px solid " + colors.divider2,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "0.5px solid " + colors.grey_500,
            },
          },
          "& .MuiInputBase-input::placeholder": {
            color: colors.text_primary,
          },
          width: "100%",
          "& .MuiInputBase-root": { height: "32px" },
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: colors.text_secondary,
          fontSize: "11px",
          marginTop: "4px",
          height: "16px",
          visibility: showMinLengthHelper ? "visible" : "hidden",
          position: "absolute",
          bottom: "-17px",
          left: "38px",
        }}
      >
        Type at least {minSearchLength} characters to search
      </Typography>
    </Box>
  );
};

export default SearchInput;
